import React, { FC, useState } from 'react'
import CircularProgress from '@material-ui/core/CircularProgress'
import ModalTemplate from 'uiKit/ModalTemplate'
import SubmitButton from 'uiKit/buttons/SubmitButton'
import { FileIcon } from 'uiKit/icons/FileIcon'
import { InfoIcon } from 'uiKit/icons/Info'
import { DownloadIcon } from 'uiKit/icons/DownloadIcon'
import { downloadBulkInviteErrorCsv } from '../../api/usersApi'
import { BulkInviteValidationResponse } from '../../model/bulkInviteTypes'
import { BulkInviteErrorsTable } from './BulkInviteErrorsTable'
import classes from './styles.module.scss'

const BRAND_BLUE = '#00A0E3'

interface Props {
  open: boolean
  botId: string
  fileName: string
  validationResult: BulkInviteValidationResponse
  onClose: () => void
  onUploadDifferentFile: () => void
  onImportValidRecords: () => Promise<void>
}

export const BulkInviteValidationModal: FC<Props> = ({
  open,
  botId,
  fileName,
  validationResult,
  onClose,
  onUploadDifferentFile,
  onImportValidRecords,
}) => {
  const [isImporting, setIsImporting] = useState(false)
  const [isDownloadingErrors, setIsDownloadingErrors] = useState(false)
  const { totalRecords, validRecords, errorRecords, errors } = validationResult

  const handleImport = async () => {
    if (validationResult.validRecords <= 0 || !validationResult.validUsers.length) {
      return
    }

    setIsImporting(true)

    try {
      await onImportValidRecords()
    } finally {
      setIsImporting(false)
    }
  }

  const handleDownloadErrorCsv = async () => {
    setIsDownloadingErrors(true)

    try {
      await downloadBulkInviteErrorCsv(botId, errors)
    } finally {
      setIsDownloadingErrors(false)
    }
  }

  return (
    <ModalTemplate
      title="Invite to CareBrain App"
      open={open}
      onClose={onClose}
      withFooter={false}
      containerClasses={classes.modalContainer}
      bodyClasses={classes.modalTemplateBody}
      disableClose={isImporting}
    >
      <>
        <div className={classes.body}>
          <p className={classes.uploadedFileLabel}>Uploaded file</p>
          <div className={classes.fileField}>
            <FileIcon color="#1F1F1F" />
            <span className={classes.fileName}>{fileName}</span>
          </div>

          <div className={classes.stats}>
            <div className={classes.statPill}>
              <span>Total records:</span>
              <span className={classes.statCount}>{totalRecords}</span>
            </div>
            <div className={`${classes.statPill} ${classes.statPillValid}`}>
              <span>Valid records:</span>
              <span className={classes.statCount}>{validRecords}</span>
            </div>
            {errorRecords > 0 && (
              <div className={`${classes.statPill} ${classes.statPillError}`}>
                <span>Records with errors:</span>
                <span className={classes.statCount}>{errorRecords}</span>
              </div>
            )}
          </div>

          {errorRecords === 0 && (
            <div className={classes.successBox}>
              <div className={classes.successIconWrapper}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M15.7071 6.29289C16.0976 6.68342 16.0976 7.31658 15.7071 7.70711L8.70711 14.7071C8.31658 15.0976 7.68342 15.0976 7.29289 14.7071L4.29289 11.7071C3.90237 11.3166 3.90237 10.6834 4.29289 10.2929C4.68342 9.90237 5.31658 9.90237 5.70711 10.2929L8 12.5858L14.2929 6.29289C14.6834 5.90237 15.3166 5.90237 15.7071 6.29289Z"
                    fill="#1D8500"
                  />
                </svg>
              </div>
              <h3 className={classes.successTitle}>File Validation Successful</h3>
              <p className={classes.successDescription}>
                You&apos;re all set! All {validRecords} records are valid and ready to be imported.
              </p>
            </div>
          )}

          {errorRecords > 0 && (
            <>
              <h3 className={classes.sectionTitle}>User Records with Errors</h3>
              <div className={classes.tableWrapper}>
                <BulkInviteErrorsTable
                  errors={errors}
                  emptyCellClassName={classes.emptyCell}
                  issueCellClassName={classes.issueCell}
                />
              </div>

              <div className={classes.infoBox}>
                <div className={classes.infoContent}>
                  <InfoIcon color="#2E2E32" width="20" height="20" />
                  <div>
                    <p className={classes.infoTitle}>Correct and Re-upload Error Records</p>
                    <p className={classes.infoDescription}>
                      Download the error records, fix the issues, and upload the file again.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className={classes.downloadLink}
                  onClick={handleDownloadErrorCsv}
                  disabled={isDownloadingErrors}
                >
                  <DownloadIcon color="#00A0E3" width={13} height={13} />
                  Download error records (.CSV)
                </button>
              </div>
            </>
          )}

          {errorRecords > 0 && (
            <>
              <div className={classes.confirmationDivider} />
              <p className={classes.footerText}>
                {validRecords} {validRecords === 1 ? 'record is' : 'records are'} ready to import. Do you want to proceed
                with importing only the valid records?
              </p>
            </>
          )}
        </div>

        <div className={classes.footer}>
          <div className={classes.footerButtons}>
            <SubmitButton
              title="Upload a different file"
              type="primary_with_border"
              styles={{
                borderRadius: 8,
                paddingLeft: 12,
                paddingRight: 12,
                color: BRAND_BLUE,
                borderColor: BRAND_BLUE,
              }}
              onClick={onUploadDifferentFile}
              disabled={isImporting}
            />
            <SubmitButton
              title={isImporting ? 'Importing...' : `Import ${validRecords} valid records`}
              type="primary"
              icon={isImporting ? <CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} /> : undefined}
              styles={{
                borderRadius: 8,
                paddingLeft: 12,
                paddingRight: 12,
                ...(!(validRecords === 0 || isImporting) && {
                  background: BRAND_BLUE,
                  backgroundColor: BRAND_BLUE,
                  borderColor: BRAND_BLUE,
                }),
              }}
              onClick={handleImport}
              disabled={validRecords === 0 || isImporting}
            />
          </div>
        </div>
      </>
    </ModalTemplate>
  )
}
